import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../environments/environment';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

interface InvitationValidationDto {
  organization?: string;
  role?: string;
  email?: string;
}

@Component({
  selector: 'app-join-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="join-page">
      <h2>Accept Your Invitation</h2>
      @if (loading) {
        <div>Loading invitation...</div>
      }
      @if (error && !loading) {
        <div class="error">{{ error }}</div>
      }
      @if (!loading && !error && invitation) {
        <p>You are invited to join <b>{{ invitation.organization }}</b> as <b>{{ invitation.role }}</b>.</p>
        @if (!isVerified) {
          <form (ngSubmit)="sendOtp()">
            <label>Email</label>
            <input [(ngModel)]="email" name="email" required type="email" readonly />
            <button type="submit" [disabled]="otpSending">{{ otpSending ? 'Redirecting...' : 'Verify Email with Microsoft' }}</button>
          </form>
        }

        @if (isVerified) {
          <form (ngSubmit)="acceptInvitation()">
            <label>Email</label>
            <input [(ngModel)]="email" name="verifiedEmail" required type="email" readonly />
            <button type="submit">Join Organization</button>
          </form>
        }

        @if (message) {
          <div>{{ message }}</div>
        }
      }
    </div>
  `,
  styles: [`
    .join-page { max-width: 400px; margin: 2rem auto; padding: 2rem; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; color: #0f172a; }
    h2 { margin-bottom: 1.5rem; color: #0f172a; }
    label { display: block; margin-top: 1rem; color: #334155; }
    input { width: 100%; padding: 0.5rem; margin-top: 0.3rem; }
    button { margin-top: 1rem; padding: 0.5rem 1.2rem; background: #8b5cf6; color: #fff; border: none; border-radius: 4px; }
    .error { color: #b91c1c; margin-bottom: 1rem; }
  `]
})
export class JoinPageComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly rawHttp = new HttpClient(inject(HttpBackend));
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly msal = inject(MsalService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly apiBaseUrl = environment.api.baseUrl.replace(/\/$/, '');

  invitation: InvitationValidationDto | null = null;
  error = '';
  email = '';
  isVerified = false;
  otpSending = false;
  message = '';
  token = '';
  loading = true;

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.token = params.get('token') || '';
      if (!this.token) {
        this.error = 'No invitation token provided.';
        this.loading = false;
        return;
      }

      this.isVerified = !!this.authService.getActiveAccount() || params.get('verified') === '1';
      if (this.isVerified) {
        this.message = 'Email verified. Click Join Organization to complete onboarding.';
        const account = this.authService.getActiveAccount();
        if (account?.username) {
          this.email = account.username;
        }
      }

      const encodedToken = encodeURIComponent(this.token);
      this.rawHttp.get(`${this.apiBaseUrl}/api/invitation-onboarding/validate?token=${encodedToken}`, { responseType: 'text' })
        .pipe(
          finalize(() => {
            this.loading = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe({
        next: (body: string) => {
          try {
            this.invitation = JSON.parse(body);
            if (!this.email) {
              this.email = this.invitation?.email ?? this.email;
            }

            if (this.isVerified && this.invitation?.email && this.email &&
              this.invitation.email.toLowerCase() !== this.email.toLowerCase()) {
              this.error = 'Signed-in account does not match invitation email. Sign in with the invited email account.';
            }
          } catch {
            this.error = 'Invitation validation returned an unexpected response.';
          }
        },
        error: (err) => {
          if (err?.status === 404) {
            this.error = 'Invalid or expired invitation token.';
          } else if (err?.status === 0) {
            this.error = 'Cannot reach API. Ensure the backend is running and HTTPS certificate is trusted.';
          } else {
            this.error = `Invitation validation failed (HTTP ${err?.status ?? 'unknown'}).`;
          }
        }
      });
    });
  }

  sendOtp(): void {
    if (!this.email?.trim()) {
      this.message = 'Email is required.';
      return;
    }

    this.otpSending = true;
    this.message = 'Redirecting to Microsoft verification...';

    sessionStorage.setItem('pendingInvitationToken', this.token);

    this.msal.loginRedirect({
      scopes: environment.apiConfig.scopes,
      prompt: 'select_account'
    });
  }

  acceptInvitation(): void {
    if (!this.isVerified) {
      this.message = 'Please verify your email with Microsoft first.';
      return;
    }
    this.http.post('/api/invitation-onboarding/accept', { token: this.token, email: this.email }).subscribe({
      next: () => {
        this.message = 'Invitation accepted! Redirecting...';
        setTimeout(() => this.router.navigate(['/home']), 1000);
      },
      error: (err) => this.message = err?.error?.message ?? 'Failed to accept invitation.'
    });
  }
}
